import { LoadingIndicator } from "@laerdal/life-react-components";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import ProductApi from "../../services/api/ProductApi";
import { buildMarketPath, selectMarket } from "../../store/slices/globalConfigSlice";

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const AssetRedirectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentMarket = useSelector(selectMarket);
  const hasNavigated = useRef(false);

  const assetName = searchParams.get("assetName") || "";
  const productLineName = searchParams.get("productLineName") || "";
  const serialNumber = searchParams.get("serialNumber") || "";

  const navigateToCaseForm = () => {
    const params = new URLSearchParams();
    if (assetName) params.set("productName", assetName);
    if (serialNumber) params.set("productSerial", serialNumber);
    if (productLineName) params.set("productLineName", productLineName);
    const url = buildMarketPath(currentMarket, `/case/create/technical/technicalSupport`);
    navigate(`${url}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (hasNavigated.current) return;

    let isActive = true;

    const resolve = async () => {
      try {
        // Step 1: Get product line code from product line name
        if (!productLineName) {
          hasNavigated.current = true;
          navigateToCaseForm();
          return;
        }

        let productLineCode: string | null = null;
        try {
          productLineCode = await ProductApi.GetProductLineCodeByAgileCode(productLineName);
        } catch {
          // Product line code not found
        }

        if (!isActive) return;

        if (!productLineCode) {
          hasNavigated.current = true;
          navigateToCaseForm();
          return;
        }

        // Step 2: Try to load the product line page
        try {
          const productLine = await ProductApi.GetProductLine(productLineCode);

          if (!isActive) return;

          if (productLine?.categoryLink && productLine?.linkName) {
            // Product page exists - navigate to it
            hasNavigated.current = true;
            const url = buildMarketPath(
              currentMarket,
              `/category/${productLine.categoryLink}/product/${productLine.linkName}`
            );
            navigate(url, { replace: true });
            return;
          }
        } catch {
          // Product line page not found
        }

        if (!isActive) return;

        // Step 3: No product page found - open case creation form
        hasNavigated.current = true;
        navigateToCaseForm();
      } catch {
        if (isActive) {
          hasNavigated.current = true;
          navigateToCaseForm();
        }
      }
    };

    resolve();

    return () => {
      isActive = false;
    };
  }, [productLineName, currentMarket]);

  return (
    <PageContainer>
      <LoadingIndicator />
    </PageContainer>
  );
};

export default AssetRedirectPage;
